<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";

import { MAX_MESSAGE_CHARS } from "../api/chat";
import { brandRef } from "../brand-state";

const props = defineProps<{
  disabled?: boolean;
  placeholder?: string;
  /** When set, an upload button appears in the composer. The component
   * does not handle the upload itself — it just fires the `upload`
   * event with the File so the parent (which owns the conversation +
   * auth headers) can call `POST /api/conversations/<id>/uploads`. */
  uploadsEnabled?: boolean;
  /** Bid Writing: folder + zip drop that preserves relative paths. */
  folderUploadsEnabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "submit", value: string): void;
  (e: "upload", files: File[]): void;
}>();

/** EU AI Act transparency notice. Brand-configurable so a deployment can
 * word it in its own language; an empty string removes it, which is only
 * correct when the disclosure is made somewhere else. */
const aiDisclosure = computed(() => brandRef.value.aiDisclosure?.trim() ?? "");

const text = ref("");
const ta = ref<HTMLTextAreaElement | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const folderInput = ref<HTMLInputElement | null>(null);

/** Show the count once a message is long enough that the ceiling is a
 * real possibility — a counter under every two-line question is noise. */
const COUNTER_FROM = 3_000;
const tooLong = computed(() => text.value.length > MAX_MESSAGE_CHARS);
const showCounter = computed(() => text.value.length >= COUNTER_FROM);

function submit() {
  const v = text.value.trim();
  if (!v || props.disabled || tooLong.value) return;
  emit("submit", v);
  text.value = "";
  nextTick(() => ta.value?.focus());
}

function onKey(e: KeyboardEvent) {
  // Submit on Enter; allow Shift+Enter for newlines.
  if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
    e.preventDefault();
    submit();
  }
}

function pickFile() {
  fileInput.value?.click();
}

function pickFolder() {
  folderInput.value?.click();
}

function onFileChosen(e: Event) {
  const inputEl = e.target as HTMLInputElement;
  const files = inputEl.files ? Array.from(inputEl.files) : [];
  if (files.length) emit("upload", files);
  inputEl.value = "";
}

const dragOver = ref(false);

function onDragOver(e: DragEvent) {
  if (!props.uploadsEnabled || props.disabled) return;
  e.preventDefault();
  dragOver.value = true;
}

function onDragLeave() {
  dragOver.value = false;
}

function onDrop(e: DragEvent) {
  if (!props.uploadsEnabled || props.disabled) return;
  e.preventDefault();
  dragOver.value = false;
  const files = e.dataTransfer?.files;
  if (files && files.length) emit("upload", Array.from(files));
}

/**
 * Paste a screenshot straight in (feedback 2026-10-26: "could be nice to be
 * able to paste pictures in directly from clipboard, instead of having to
 * save them and attach them as a file"). Clipboard files go through the same
 * upload path as 📎 and drag-and-drop, so every assistant with uploads gets
 * it.
 *
 * Only when the clipboard holds NO text. Copying from Word or Excel puts the
 * text AND a picture of it on the clipboard; treating that as an image would
 * upload a screenshot every time someone pastes a paragraph. A screenshot
 * tool or "Copy image" puts only the image there.
 */
function onPaste(e: ClipboardEvent) {
  if (!props.uploadsEnabled || props.disabled) return;
  const data = e.clipboardData;
  if (!data) return;
  if ((data.getData("text/plain") || "").trim()) return; // a normal text paste
  const files = Array.from(data.items)
    .filter((i) => i.kind === "file")
    .map((i) => i.getAsFile())
    .filter((f): f is File => f !== null);
  if (!files.length) return;
  e.preventDefault();
  // Browsers name every pasted image "image.png"; several pastes in one
  // conversation would be indistinguishable in the attachment list.
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const named = files.map((f, i) => {
    const generic = !f.name || /^image\.(png|jpe?g|gif|bmp|webp)$/i.test(f.name);
    if (!generic) return f;
    const ext = (f.type.split("/")[1] || "png").replace("jpeg", "jpg");
    const suffix = files.length > 1 ? `-${i + 1}` : "";
    return new File([f], `skaermbillede-${stamp}${suffix}.${ext}`, { type: f.type });
  });
  emit("upload", named);
}

/**
 * Put text in the box without sending it, and park the caret at the end.
 * The transcript's empty-state chips use this: a sample may be a template
 * the user needs to complete, so landing it in the composer to edit is the
 * only behaviour that works for both kinds of sample.
 */
function setText(v: string) {
  text.value = v;
  nextTick(() => {
    ta.value?.focus();
    ta.value?.setSelectionRange(v.length, v.length);
  });
}
defineExpose({ setText });

// Auto-resize on input.
watch(text, () => {
  const el = ta.value;
  if (!el) return;
  el.style.height = "auto";
  el.style.height = Math.min(el.scrollHeight, 240) + "px";
});
</script>

<template>
  <div class="composer-shell">
    <form
      class="composer"
      :class="{ 'composer--drag': dragOver }"
      @submit.prevent="submit"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    >
      <textarea
        ref="ta"
        v-model="text"
        class="composer__input"
        :placeholder="placeholder ?? 'Ask a question…'"
        :disabled="disabled"
        aria-label="Type your question"
        rows="1"
        @keydown="onKey"
        @paste="onPaste"
      />
      <div class="composer__actions">
        <!-- No .zip in `accept`. The conversation upload route converts ONE
             file at a time and refuses archives, so offering it here only
             ever produced a failed upload. Unpacking one would mean taking an
             archive from a browser and walking its entries — zip bombs,
             traversal paths — to serve a case the folder picker below already
             covers: bid-writing uploads a FOLDER (webkitdirectory), which
             takes no accept list at all. -->
        <input
          v-if="uploadsEnabled"
          ref="fileInput"
          type="file"
          class="composer__file"
          accept=".pdf,.docx,.doc,.txt,.log,.md,.json,.csv,.xlsx,.pptx,.png,.jpg,.jpeg,.bmp,.tif,.tiff,.heic,.heif"
          multiple
          @change="onFileChosen"
        />
        <input
          v-if="folderUploadsEnabled"
          ref="folderInput"
          type="file"
          class="composer__file"
          webkitdirectory
          multiple
          @change="onFileChosen"
        />
        <button
          v-if="uploadsEnabled"
          type="button"
          class="composer__attach"
          :disabled="disabled"
          :aria-label="'Attach a file (PDF, docx, txt, md, zip)'"
          title="Attach a file"
          @click="pickFile"
        >
          📎
        </button>
        <button
          v-if="folderUploadsEnabled"
          type="button"
          class="composer__attach"
          :disabled="disabled"
          aria-label="Attach a folder"
          title="Attach a folder (keeps A–F paths)"
          @click="pickFolder"
        >
          📁
        </button>
        <button
          type="submit"
          class="composer__send"
          :disabled="disabled || !text.trim() || tooLong"
          :aria-disabled="disabled || !text.trim() || tooLong"
          :aria-label="
            disabled ? 'Answer is streaming, please wait' : 'Send question'
          "
        >
          <span v-if="disabled" aria-hidden="true">…</span>
          <span v-else aria-hidden="true">→</span>
        </button>
      </div>
      <p class="composer__hint">
        <kbd>Enter</kbd> to send · <kbd>Shift</kbd>+<kbd>Enter</kbd> for a new
        line<template v-if="uploadsEnabled">
          · 📎, drag or paste a screenshot to attach</template
        ><template v-if="folderUploadsEnabled">
          · 📁 folder / zip keeps A–F paths</template
        ><span v-if="showCounter" class="composer__count" :class="{ 'composer__count--over': tooLong }" aria-live="polite">
          · {{ text.length.toLocaleString("da-DK") }} / {{ MAX_MESSAGE_CHARS.toLocaleString("da-DK") }}<template v-if="tooLong"> — too long, attach it as a file instead</template>
        </span>
      </p>
    </form>
    <!-- Always visible, deliberately. Unlike `.composer__hint` above, which
         teaches a keyboard shortcut once and then gets out of the way, this
         is a standing disclosure: a limited-risk AI system has to tell the
         person reading it that a machine wrote the answer. Fading it in on
         focus would mean it is absent exactly when someone is reading a
         result rather than typing. -->
    <p v-if="aiDisclosure" class="composer__ai-notice">
      {{ aiDisclosure }}
    </p>
  </div>
</template>

<style scoped>
.composer-shell {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
/* Quiet by design — this has to be permanently legible without competing
   with the answer above it. Muted ink at small size, centred under the
   composer, and it never disappears. */
.composer__ai-notice {
  margin: 0;
  padding: 0 var(--space-2);
  font-size: 0.72rem;
  line-height: 1.4;
  color: var(--color-muted);
  text-align: center;
  text-wrap: balance;
}

.composer {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--space-2);
  align-items: end;
  background: var(--glass-bg-strong);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-xl);
  padding: var(--space-2) var(--space-2) var(--space-2) var(--space-4);
  box-shadow: var(--shadow-md);
  transition:
    border-color var(--dur-2) var(--ease-out),
    box-shadow var(--dur-2) var(--ease-out),
    transform var(--dur-2) var(--ease-out);
}
/* Focus-within lifts the whole composer and rings it — the input itself is
   borderless, so the container carries the focus affordance. */
.composer:focus-within {
  border-color: color-mix(in srgb, var(--color-accent) 50%, transparent);
  box-shadow: var(--shadow-glow);
}
.composer--drag {
  border-color: var(--color-accent);
  background: var(--color-surface);
}
.composer__input {
  resize: none;
  border: 0;
  outline: 0;
  background: transparent;
  font-size: 1rem;
  line-height: 1.4;
  min-height: 2rem;
  max-height: 15rem;
  padding: 0.25rem 0.5rem;
  width: 100%;
}
.composer__input:disabled {
  opacity: 0.6;
}
/* The container carries the focus affordance (:focus-within), so the inner
   textarea suppresses the global ring to avoid a double outline. */
.composer__input:focus-visible {
  box-shadow: none;
}
/* The same circular arrow as the Hub's ask bar. Two send buttons in one
   product should not be a rectangle in one place and a disc in the other;
   the label lives on aria-label, which is what a screen reader reads
   anyway. */
.composer__send {
  align-self: end;
  display: grid;
  place-items: center;
  flex: none;
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 50%;
  background: var(--color-accent);
  color: var(--color-inverse);
  font-size: 1.05rem;
  line-height: 1;
  transition:
    background var(--dur-1) var(--ease-out),
    transform var(--dur-1) var(--ease-spring);
}
.composer__send:hover:not(:disabled) {
  background: var(--color-accent-hover);
  transform: scale(1.06);
}
.composer__send:disabled {
  background: transparent;
  border: 1px solid var(--glass-border);
  color: var(--color-muted);
  cursor: not-allowed;
}
.composer__actions {
  display: flex;
  align-items: end;
  gap: var(--space-2);
}
.composer__file {
  display: none;
}
.composer__attach {
  background: transparent;
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-pill);
  width: 2.5rem;
  height: 2.5rem;
  cursor: pointer;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background var(--dur-1) var(--ease-out);
}
.composer__attach:hover:not(:disabled) {
  background: var(--color-surface);
}
.composer__attach:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
/* Quiet keyboard guidance. It teaches Enter / Shift+Enter, which is worth
   saying once — but as a permanent third row under the input it was just
   chrome, so it only surfaces while the composer has focus. Height is
   reserved either way so the composer never jumps as it fades in. */
.composer__hint {
  grid-column: 1 / -1;
  margin: 0;
  padding-top: var(--space-1);
  font-size: 0.72rem;
  color: var(--color-muted);
  opacity: 0;
  transition: opacity var(--dur-2) var(--ease-out);
}
.composer:focus-within .composer__hint {
  opacity: 1;
}
/* The count must stay readable when the box loses focus: a person who
   pasted too much and clicked away should still see why Send is off. */
.composer__hint:has(.composer__count) {
  opacity: 1;
}
.composer__count--over {
  color: var(--color-danger, #b42318);
  font-weight: 600;
}

.composer__hint kbd {
  font-family: var(--font-mono);
  font-size: 0.68rem;
  padding: 0.05rem 0.3rem;
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-sm);
  background: color-mix(in srgb, var(--color-surface) 55%, transparent);
}
@media (max-width: 520px) {
  .composer__hint {
    display: none;
  }
}
</style>
